/**
 * The six roles the annual planning cycle is built on.
 *
 * Mirrors `App\Support\Rbac\PlanningRole` on the backend. Three of these roles rename a
 * role the system already had (SMO, PMO, VP), so each one lists the legacy names that
 * are equivalent to it. The auth store expands role checks through `equivalentRoles()`,
 * which is why the many `hasRole('SMO')` style checks across the UI keep working for a
 * user who holds the canonical role instead — and vice versa.
 *
 * The mapping is deliberately one-to-one. "Strategy Manager" and "Strategy Specialist"
 * work on strategic plans rather than owning the annual plan, so neither is equivalent
 * to the Strategy Department Representative.
 */
export const PLANNING_ROLES = {
  STRATEGY_DEPARTMENT_REPRESENTATIVE: "Strategy Department Representative",
  PMO_REPRESENTATIVE: "PMO Representative",
  SECTOR_REPRESENTATIVE: "Sector Representative",
  VP_STRATEGY_PROJECTS: "VP of Strategy & Projects",
  CEO: "CEO",
  PROJECT_MANAGER: "Project Manager",
};

/** Canonical role name → legacy role names that grant the same access. */
const EQUIVALENTS = {
  [PLANNING_ROLES.STRATEGY_DEPARTMENT_REPRESENTATIVE]: ["SMO"],
  [PLANNING_ROLES.PMO_REPRESENTATIVE]: ["PMO"],
  [PLANNING_ROLES.SECTOR_REPRESENTATIVE]: [],
  [PLANNING_ROLES.VP_STRATEGY_PROJECTS]: ["VP"],
  [PLANNING_ROLES.CEO]: [],
  [PLANNING_ROLES.PROJECT_MANAGER]: [],
};

/** Legacy role name → the canonical role it belongs to, derived from EQUIVALENTS. */
const CANONICAL_OF = Object.entries(EQUIVALENTS).reduce(
  (map, [canonical, legacyNames]) => {
    legacyNames.forEach((legacy) => {
      map[legacy] = canonical;
    });
    return map;
  },
  {},
);

/**
 * Every role name that satisfies a check for `role`, the name itself included.
 *
 * Works in both directions: asking for "SMO" also accepts the Strategy Department
 * Representative, and asking for the representative also accepts "SMO".
 *
 * @param {string} role
 * @returns {string[]}
 */
export function equivalentRoles(role) {
  if (typeof role !== "string") return [];

  const canonical = CANONICAL_OF[role] ?? role;
  const names = [role, canonical, ...(EQUIVALENTS[canonical] ?? [])];

  return [...new Set(names)];
}
